import type { ReactNode, CSSProperties } from 'react'

// PanelShell — bloco responsivo padrão dos painéis (aprovado 2026-07-20).
// Preenche o box global do App (h-full), toolbar fixa no topo e corpo flex;
// o scroll NUNCA é do bloco inteiro — só das regiões internas marcadas com
// overflow próprio (tabelas usam thead/tfoot sticky via stickyTh/stickyFoot).
// Adotado primeiro no Fluxo de caixa; replicar nos demais painéis de fluxo.
//
// PLANOS (21/10/2026, aprovado pelo dono no Fluxo de caixa): com `titulo`, o
// shell desenha o CABEÇALHO da tela (ladrilho + título 22 + subtítulo, ações à
// direita) e o corpo passa a empilhar as peças abaixo com respiro de 16px.
// Regra: nada fica solto no chão além do cabeçalho — filtros vão numa
// PlnBarra, números em PlnKpi, gráfico/tabela em PlnBloco. Estilos no fim do
// index.css, bloco "PLANOS · peças de tela".
//
// `corpoMin` (05/11/2026, bug do Incentivos): quando a toolbar é ALTA (vários
// blocos de papel), num notebook ela comia a tela inteira — o corpo ia a 0px,
// a tabela sumia e, sem rolagem nenhuma, o que ficava abaixo da dobra era
// inalcançável. Com `corpoMin` (px), o corpo nunca fica menor que isso e, se a
// tela não comporta, a TELA inteira rola. Em tela grande nada muda: o corpo
// continua flex-1 e só a tabela rola por dentro.
export function PanelShell({ toolbar, children, titulo, sub, icone, acoes, corpoMin }: {
  toolbar?: ReactNode; children: ReactNode
  titulo?: ReactNode; sub?: ReactNode; icone?: ReactNode; acoes?: ReactNode
  corpoMin?: number
}) {
  if (titulo) {
    return (
      <div className={`h-full min-h-0 flex flex-col p-4 sm:p-6 neu-forms pln-tela-fin${corpoMin ? ' overflow-y-auto scrollbar-thin' : ''}`}>
        <div className="pln-cab shrink-0">
          {icone && <div className="pln-cab-icone">{icone}</div>}
          <div className="pln-cab-txt min-w-0">
            <h1 className="pln-cab-titulo">{titulo}</h1>
            {sub && <div className="pln-cab-sub">{sub}</div>}
          </div>
          {acoes && <div className="pln-cab-acoes">{acoes}</div>}
        </div>
        {toolbar && <div className="shrink-0">{toolbar}</div>}
        <div className={`flex-1 flex flex-col pln-corpo-tela ${corpoMin ? 'shrink-0' : 'min-h-0'}`}
          style={corpoMin ? { minHeight: corpoMin } : undefined}>{children}</div>
      </div>
    )
  }
  return (
    <div className="h-full min-h-0 flex flex-col p-4 sm:p-6 neu-forms">
      {toolbar && <div className="shrink-0">{toolbar}</div>}
      <div className="flex-1 min-h-0 flex flex-col">{children}</div>
    </div>
  )
}

// Faixa de filtros: os controles moram DENTRO de um papel, nunca soltos.
export function PlnBarra({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={`pln-barra shrink-0 ${className}`}>{children}</div>
}
export const PlnBarraRot = ({ children }: { children: ReactNode }) => <span className="pln-barra-rot">{children}</span>
export const PlnBarraSep = () => <span className="pln-barra-sep" aria-hidden />

// Cartões de número (KPI 27px). `tom` pinta o valor: ok = verde da marca, ruim = vermelho.
export function PlnKpis({ children }: { children: ReactNode }) {
  return <div className="pln-kpis">{children}</div>
}
export function PlnKpi({ rotulo, valor, pe, cor, tom }: { rotulo: ReactNode; valor: ReactNode; pe?: ReactNode; cor?: string; tom?: 'ok' | 'ruim' }) {
  return (
    <div className="pln-kpi">
      <div className="pln-kpi-rot">{cor && <i style={{ background: cor }} />}{rotulo}</div>
      <div className={`pln-kpi-val ${tom || ''}`}>{valor}</div>
      {pe && <div className="pln-kpi-pe">{pe}</div>}
    </div>
  )
}

// Bloco de papel com cabeçalho (título 16 + nota; `extra` vai à direita).
export function PlnBloco({ titulo, nota, extra, children, className = '', style }: {
  titulo?: ReactNode; nota?: ReactNode; extra?: ReactNode; children: ReactNode; className?: string; style?: CSSProperties
}) {
  return (
    <section className={`pln-bloco ${className}`} style={style}>
      {titulo && (
        <div className="pln-bloco-cab">
          <h3 className="pln-bloco-titulo">{titulo}</h3>
          {nota && <span className="pln-bloco-nota">{nota}</span>}
          {extra && <div className="pln-bloco-extra">{extra}</div>}
        </div>
      )}
      {children}
    </section>
  )
}

// Classes pra cabeçalho/rodapé de tabela que permanecem visíveis durante o
// scroll interno. A borda vai por box-shadow inset — border em célula sticky
// não acompanha com border-collapse.
export const stickyTh = 'sticky top-0 z-10 bg-card neu-thead shadow-[inset_0_-1px_0_hsl(var(--border))]'
export const stickyFoot = 'sticky bottom-0 z-10 bg-card neu-tfoot shadow-[inset_0_1px_0_hsl(var(--border))]'
